/* Windows 95 Product Popup Window with Image Carousel */

class Windows95ProductPopup {
  constructor() {
    this.activeWindows = new Map();
    this.windowCounter = 0;
    this.init();
  }

  init() {
    // Listen for product open events
    document.addEventListener('win95-open-product', (e) => {
      this.openProductWindow(e.detail.productId, e.detail.productHandle);
    });
  }

  async openProductWindow(productId, productHandle) {
    try {
      // Fetch product data from Shopify
      const product = await this.fetchProduct(productHandle);
      
      // Create and show window
      const windowId = `win95-product-${this.windowCounter++}`;
      const windowElement = this.createWindow(windowId, product);
      document.body.appendChild(windowElement);

      // Setup window functionality
      this.setupWindowControls(windowElement, windowId);
      this.setupProductForm(windowElement, product);
      this.setupImageCarousel(windowElement, product);
      
      // Make it draggable and center it
      window.Windows95Desktop.makeDraggable(windowElement);
      window.Windows95Desktop.centerWindow(windowElement);
      
      // Show the window
      windowElement.classList.add('active');
      
      // Store reference
      this.activeWindows.set(windowId, windowElement);
      
    } catch (error) {
      console.error('Error opening product window:', error);
      this.showErrorWindow(error.message);
    }
  }

  async fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      throw new Error('Product not found');
    }
    return await response.json();
  }

  createWindow(windowId, product) {
    const window = document.createElement('div');
    window.className = 'win95-window';
    window.id = windowId;

    // Get first variant for initial display
    const firstVariant = product.variants[0];
    const price = this.formatPrice(firstVariant.price);
    
    // Determine if product has variants
    const hasVariants = product.variants.length > 1;
    const hasOptions = product.options && product.options.length > 0 && product.options[0].name !== 'Title';

    window.innerHTML = `
      <div class="win95-title-bar">
        <div class="win95-title-text">
          <span class="win95-title-icon">📦</span>
          ${this.escapeHtml(product.title)}
        </div>
        <div class="win95-window-controls">
          <button class="win95-btn win95-btn-minimize" aria-label="Minimize"></button>
          <button class="win95-btn win95-btn-maximize" aria-label="Maximize"></button>
          <button class="win95-btn win95-btn-close" aria-label="Close"></button>
        </div>
      </div>
      <div class="win95-window-content">
        <div class="win95-image-carousel">
          <div class="win95-carousel-images">
            ${product.images.map((image, index) => `
              <img 
                src="${image}" 
                alt="${this.escapeHtml(product.title)}" 
                class="win95-product-image ${index === 0 ? 'active' : ''}"
                data-image-index="${index}"
                style="display: ${index === 0 ? 'block' : 'none'};"
              >
            `).join('')}
          </div>
          ${product.images.length > 1 ? `
            <div class="win95-carousel-controls">
              <button class="win95-button win95-carousel-btn" data-carousel-prev>&lt;</button>
              <span class="win95-carousel-counter">
                <span data-current-image>1</span> / ${product.images.length}
              </span>
              <button class="win95-button win95-carousel-btn" data-carousel-next>&gt;</button>
            </div>
          ` : ''}
        </div>
        
        <div class="win95-product-title">${this.escapeHtml(product.title)}</div>
        <div class="win95-product-price" data-price-display>${price}</div>
        
        ${product.description ? `
          <div class="win95-product-description">
            ${product.description}
          </div>
        ` : ''}
        
        <form class="win95-product-form" data-product-form>
          ${hasOptions ? this.createVariantSelector(product) : ''}
          
          <div class="win95-form-group">
            <label class="win95-label" for="${windowId}-quantity">Quantity:</label>
            <input type="number" id="${windowId}-quantity" name="quantity" value="1" min="1" class="win95-input" data-quantity-input>
          </div>

          <input type="hidden" name="id" value="${firstVariant.id}" data-variant-id>
          
          <div class="win95-button-group">
            <button type="submit" class="win95-button win95-button-primary" data-add-to-cart>
              Add to Cart
            </button>
            <button type="button" class="win95-btn-close-window win95-button">
              Cancel
            </button>
          </div>
        </form>

        <div class="win95-status-bar">
          <div class="win95-status-item" data-status-message>Ready</div>
        </div>
      </div>
    `;

    return window;
  }

  setupImageCarousel(windowElement, product) {
    if (product.images.length <= 1) return;

    const images = windowElement.querySelectorAll('.win95-product-image');
    const prevBtn = windowElement.querySelector('[data-carousel-prev]');
    const nextBtn = windowElement.querySelector('[data-carousel-next]');
    const counter = windowElement.querySelector('[data-current-image]');
    
    let currentIndex = 0;

    const showImage = (index) => {
      images.forEach((img, i) => {
        img.style.display = i === index ? 'block' : 'none';
        img.classList.toggle('active', i === index);
      });
      counter.textContent = index + 1;
      currentIndex = index;
    };

    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
      showImage(newIndex);
    });

    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
      showImage(newIndex);
    });

    // Keyboard navigation
    windowElement.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevBtn?.click();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextBtn?.click();
      }
    });
  }

  createVariantSelector(product) {
    let html = '';
    
    // Create selector for each option
    product.options.forEach((option, index) => {
      if (option.name === 'Title') return; // Skip default title option
      
      html += `
        <div class="win95-form-group">
          <label class="win95-label" for="option-${index}">${this.escapeHtml(option.name)}:</label>
          <select id="option-${index}" class="win95-select" data-option-selector data-option-index="${index}">
            ${option.values.map(value => `
              <option value="${this.escapeHtml(value)}">${this.escapeHtml(value)}</option>
            `).join('')}
          </select>
        </div>
      `;
    });

    return html;
  }

  setupWindowControls(windowElement, windowId) {
    const closeBtn = windowElement.querySelector('.win95-btn-close');
    const minimizeBtn = windowElement.querySelector('.win95-btn-minimize');
    const cancelBtn = windowElement.querySelector('.win95-btn-close-window');

    // Close window
    const closeWindow = () => {
      windowElement.classList.remove('active');
      setTimeout(() => {
        windowElement.remove();
        this.activeWindows.delete(windowId);
      }, 200);
    };

    closeBtn?.addEventListener('click', closeWindow);
    cancelBtn?.addEventListener('click', closeWindow);

    // Minimize (just close for now, could implement taskbar buttons later)
    minimizeBtn?.addEventListener('click', closeWindow);

    // Click title bar to bring to front
    const titleBar = windowElement.querySelector('.win95-title-bar');
    titleBar?.addEventListener('mousedown', () => {
      window.Windows95Desktop.bringToFront(windowElement);
    });
  }

  setupProductForm(windowElement, product) {
    const form = windowElement.querySelector('[data-product-form]');
    const variantIdInput = windowElement.querySelector('[data-variant-id]');
    const priceDisplay = windowElement.querySelector('[data-price-display]');
    const quantityInput = windowElement.querySelector('[data-quantity-input]');
    const statusMessage = windowElement.querySelector('[data-status-message]');
    const addToCartBtn = windowElement.querySelector('[data-add-to-cart]');
    
    // Handle variant selection
    const optionSelectors = windowElement.querySelectorAll('[data-option-selector]');
    if (optionSelectors.length > 0) {
      optionSelectors.forEach(selector => {
        selector.addEventListener('change', () => {
          const selectedOptions = Array.from(optionSelectors).map(s => s.value);
          const variant = this.findVariantByOptions(product, selectedOptions);
          
          if (variant) {
            variantIdInput.value = variant.id;
            priceDisplay.textContent = this.formatPrice(variant.price);
            
            // Update availability
            if (variant.available) {
              addToCartBtn.disabled = false;
              statusMessage.textContent = 'Ready';
            } else {
              addToCartBtn.disabled = true;
              statusMessage.textContent = 'Out of Stock';
            }
          }
        });
      });
    }

    // Handle form submission
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const variantId = variantIdInput.value;
      const quantity = parseInt(quantityInput.value);

      try {
        addToCartBtn.disabled = true;
        statusMessage.textContent = 'Adding to cart...';
        
        await this.addToCart(variantId, quantity);
        
        statusMessage.textContent = 'Added to cart!';
        
        // Show success and close after delay
        setTimeout(() => {
          windowElement.querySelector('.win95-btn-close')?.click();
        }, 1000);
        
      } catch (error) {
        statusMessage.textContent = 'Error: ' + error.message;
        addToCartBtn.disabled = false;
      }
    });
  }

  findVariantByOptions(product, selectedOptions) {
    return product.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant.options[index] === option;
      });
    });
  }

  async addToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }

    // Update cart count if there's a cart indicator
    this.updateCartCount();
    
    return await response.json();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header if it exists
      const cartCount = document.querySelector('.cart-count-bubble span');
      if (cartCount) {
        cartCount.textContent = cart.item_count;
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showErrorWindow(message) {
    const errorWindow = document.createElement('div');
    errorWindow.className = 'win95-window active';
    errorWindow.style.width = '400px';
    
    errorWindow.innerHTML = `
      <div class="win95-title-bar">
        <div class="win95-title-text">
          <span class="win95-title-icon">⚠️</span>
          Error
        </div>
        <div class="win95-window-controls">
          <button class="win95-btn win95-btn-close" aria-label="Close"></button>
        </div>
      </div>
      <div class="win95-window-content">
        <p style="margin: 20px 0;">${this.escapeHtml(message)}</p>
        <div class="win95-button-group">
          <button type="button" class="win95-button win95-button-primary" onclick="this.closest('.win95-window').remove()">
            OK
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(errorWindow);
    window.Windows95Desktop.centerWindow(errorWindow);
    
    errorWindow.querySelector('.win95-btn-close')?.addEventListener('click', () => {
      errorWindow.remove();
    });
  }

  formatPrice(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new Windows95ProductPopup();
  });
} else {
  new Windows95ProductPopup();
}